import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import {
  Tabs,
  PeriodPicker,
  DashboardRangeBar,
  TabSection,
  TimezoneToggle,
  BROWSER_TIMEZONE,
  parseDashboardPeriod,
} from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import OverviewTab from '../components/dashboard/OverviewTab';
import FinancialTab from '../components/dashboard/FinancialTab';
import AgentsTab from '../components/dashboard/AgentsTab';
import RoadblocksTab from '../components/dashboard/RoadblocksTab';
import { useAuth } from '../hooks/useAuth';
import {
  useDashboardV2Agents,
  useDashboardV2Financial,
  useDashboardV2Overview,
  useDashboardV2Roadblocks,
} from '../hooks/useDashboardV2';
import type { DashboardV2PeriodParams, SuperAgentDashboardV2Envelope } from '../services/api';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'financial', label: 'Financials' },
  { value: 'agents', label: 'Agents' },
  { value: 'roadblocks', label: 'Roadblocks' },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agentsPage, setAgentsPage] = useState(1);

  const tab = TABS.some((t) => t.value === searchParams.get('tab'))
    ? (searchParams.get('tab') as string)
    : 'overview';
  const useLocalTz = searchParams.get('tz') === 'local';
  const periodParams: DashboardV2PeriodParams = useMemo(() => {
    const period = parseDashboardPeriod(searchParams.get('period'));
    const dateFrom = searchParams.get('from') || undefined;
    const dateTo = searchParams.get('to') || undefined;
    return {
      period,
      ...(period === 'custom' && dateFrom ? { dateFrom } : {}),
      ...(period === 'custom' && dateTo ? { dateTo } : {}),
      ...(useLocalTz ? { tz: BROWSER_TIMEZONE } : {}),
    };
  }, [searchParams, useLocalTz]);

  // Overview stays enabled on every tab so the roadblocks badge can populate.
  const overviewQuery = useDashboardV2Overview(periodParams, true);
  const financialQuery = useDashboardV2Financial(periodParams, tab === 'financial');
  const agentsQuery = useDashboardV2Agents(periodParams, agentsPage, tab === 'agents');
  const roadblocksQuery = useDashboardV2Roadblocks(periodParams, tab === 'roadblocks');

  const activeQuery =
    tab === 'overview'
      ? overviewQuery
      : tab === 'financial'
        ? financialQuery
        : tab === 'agents'
          ? agentsQuery
          : roadblocksQuery;

  const roadblocksBadge = overviewQuery.data?.extras.roadblocksBadgeCount ?? 0;

  const tabsWithBadge = useMemo(
    () =>
      TABS.map((t) =>
        t.value === 'roadblocks' && roadblocksBadge > 0
          ? { ...t, label: `Roadblocks (${roadblocksBadge})` }
          : t,
      ),
    [roadblocksBadge],
  );

  useEffect(() => {
    setAgentsPage(1);
  }, [periodParams.period, periodParams.dateFrom, periodParams.dateTo, tab]);

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const setPeriodParams = useCallback(
    (next: DashboardV2PeriodParams) => {
      if (next.period === 'custom' && (!next.dateFrom || !next.dateTo)) return;
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('period', next.period);
        if (next.period === 'custom' && next.dateFrom && next.dateTo) {
          params.set('from', next.dateFrom);
          params.set('to', next.dateTo);
        } else {
          params.delete('from');
          params.delete('to');
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const costRate =
    user?.costRate != null && Number.isFinite(Number(user.costRate)) ? Number(user.costRate) : 0;

  return (
    <AppLayout>
      <div className="space-y-6 rounded-2xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">Dashboard</h1>
            <p className="mt-1 text-sm text-gray-600">Network pulse and money at a glance</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-start sm:self-center">
            <Star className="h-4 w-4 text-blue-600" fill="currentColor" aria-hidden />
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-800">
              Super Agent
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker
              value={periodParams.period}
              dateFrom={periodParams.dateFrom}
              dateTo={periodParams.dateTo}
              onChange={setPeriodParams}
              accent="blue"
            />
            <TimezoneToggle
              accent="blue"
              defaultLabel="Report time"
              useLocal={useLocalTz}
              onChange={(local) => {
                setSearchParams((prev) => {
                  const params = new URLSearchParams(prev);
                  if (local) params.set('tz', 'local');
                  else params.delete('tz');
                  return params;
                });
              }}
            />
          </div>
        </div>

        <DashboardRangeBar
          envelope={activeQuery.data as SuperAgentDashboardV2Envelope | undefined}
          onRefresh={activeQuery.refresh}
          refreshing={activeQuery.refreshing || activeQuery.isFetching}
          accent="blue"
        />

        <Tabs items={tabsWithBadge} value={tab} onChange={setTab} accent="blue" />

        <TabSection
          error={activeQuery.error}
          onRetry={activeQuery.refetch}
          isLoading={activeQuery.isLoading}
        >
          {tab === 'overview' && (
            <OverviewTab
              period={periodParams.period}
              costRate={costRate}
              data={overviewQuery.data}
              isLoading={overviewQuery.isLoading}
            />
          )}
          {tab === 'financial' && (
            <FinancialTab
              period={periodParams.period}
              data={financialQuery.data}
              isLoading={financialQuery.isLoading}
            />
          )}
          {tab === 'agents' && (
            <AgentsTab
              period={periodParams.period}
              data={agentsQuery.data}
              isLoading={agentsQuery.isLoading}
              page={agentsPage}
              onPageChange={setAgentsPage}
            />
          )}
          {tab === 'roadblocks' && (
            <RoadblocksTab
              period={periodParams.period}
              data={roadblocksQuery.data}
              isLoading={roadblocksQuery.isLoading}
            />
          )}
        </TabSection>
      </div>
    </AppLayout>
  );
}
